<template>
  <transition name="zm-zoom-in-top">
    <div
      v-show="visible"
      class="zm-time-picker-panel zm-popper"
      role="application"
      aria-label="Calendar view time-picker"
      :class="popperClass"
      :style="elStyl"
    >
      <div class="zm-time-picker-panel__content">
        <zm-scrollbar
          class="zm-time-picker-panel__spinner-wrapper"
          :wrap-style="wrapStyl"
          view-class="zm-time-picker-panel__spinner-list"
          noresize
          tag="ul"
          ref="hours"
        >
          <li
            @click="handleClick('hours', hour)"
            v-for="hour in timeList.hours"
            :key="hour"
            ref="hour"
            class="zm-time-picker-panel__spinner-item"
            :class="{ 'active': hour === hours}">{{ padZero(hour) }}
          </li>
        </zm-scrollbar>
        <zm-scrollbar
          class="zm-time-picker-panel__spinner-wrapper"
          :wrap-style="wrapStyl"
          view-class="zm-time-picker-panel__spinner-list"
          noresize
          tag="ul"
          ref="minutes"
        >
          <li
            @click="handleClick('minutes', minute)"
            v-for="minute in timeList.minutes"
            :key="minute"
            ref="minute"
            class="zm-time-picker-panel__spinner-item"
            :class="{ 'active': minute === minutes }">{{ padZero(minute)}}
          </li>
        </zm-scrollbar>
        <zm-scrollbar
          v-if="showSeconds"
          class="zm-time-picker-panel__spinner-wrapper"
          :wrap-style="wrapStyl"
          view-class="zm-time-picker-panel__spinner-list"
          noresize
          tag="ul"
          ref="seconds"
        >
          <li
            @click="handleClick('seconds', second)"
            v-for="second in timeList.seconds"
            :key="second"
            ref="second"
            class="zm-time-picker-panel__spinner-item"
            :class="{ 'active': second === seconds }"
          >{{ padZero(second) }}
          </li>
        </zm-scrollbar>
      </div>
      <div class="zm-time-picker-panel__footer">
        <zm-button
          type="primary"
          @click="handleConfirm">{{ t('datepicker.confirm') }}
        </zm-button>
      </div>
    </div>
  </transition>
</template>

<script>
  import { padZero, modifyTime } from '../util';
  import Locale from '../../../mixins/locale';
  import TimePickerMixin from '../util/time-picker-mixin';
  import scrollbarWidth from '../../../utils/scrollbar-width';

  export default {
    name: 'time-picker',
    mixins: [Locale, TimePickerMixin],
    props: {
      visible: Boolean
    },

    data() {
      return {
        popperClass: '',
        format: 'HH:mm:ss',
        value: '',
        defaultValue: null,
        date: new Date(),
        selectableRange: [],
        showSeconds: true
      };
    },

    computed: {
      wrapStyl() {
        const gutter = scrollbarWidth();
        const maxHeight = gutter ? '255px' : '240px';
        return `height:${maxHeight};`;
      }
    },

    methods: {
      padZero(s) {
        return padZero(s);
      },
      adjust(noAnimation) {
        ['hours', 'minutes', 'seconds'].forEach((item) => {
          this.adjustCurrentSpinner(item, noAnimation);
        });
      },
      adjustCurrentSpinner(type, noAnimation) {
        this.adjustSpinner(type, this[type], noAnimation);
      },
      adjustSpinner(type, value, noAnimation) {
        const scrollBar = this.$refs[type];
        if (!scrollBar) {
          return;
        }
        const time = noAnimation ? 0 : 300;
        const list = this.$refs[type.slice(0, type.length - 1)];
        const index = this.timeList[type].indexOf(value);
        const el = list[index];
        scrollBar.scrollIntoView(el, null, time, -48);
      },
      typeItemHeight(type) {
        return this.$refs[type].$el.querySelector('li').offsetHeight;
      },
      handleClick(type, value) {
        this.modifyDateField(type, value);
      },
      handleConfirm() {
        this.$emit('escClose');
      },
      modifyDateField(type, value) {
        switch (type) {
          case 'hours':
            this.$emit('pick', modifyTime(this.value, value, this.minutes, this.seconds), true);
            break;
          case 'minutes':
            this.$emit('pick', modifyTime(this.value, this.hours, value, this.seconds), true);
            break;
          case 'seconds':
            this.$emit('pick', modifyTime(this.value, this.hours, this.minutes, value), true);
            break;
        }
      }
    },

    watch: {
      value(newVal, oldVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.adjust(!oldVal);
          });
        }
      },
      visible(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.adjust(true);
          });
        }
      }
    }
  };
</script>

<style lang="scss">
  @import "../../../scss/common/var";
  @import "../../../scss/mixins/mixins";

  @include b(time-picker-panel) {
    position: absolute;
    left: 0;
    z-index: $--index-top;
    margin: 5px 0;
    background-color: $--color-white;
    box-shadow: $--box-shadow-light;
    border-radius: $--border-radius-base;

    @include e(content) {
      display: flex;
      overflow: hidden;
      height: 240px;
      border-bottom: 1px solid $--color-border-light;
    }

    @include e(spinner-wrapper) {
      box-sizing: content-box;
      width: 60px;
      border-right: 1px solid $--color-border-light;
      &:last-child {
        border: none;
      }
    }

    @include e(spinner-list) {
      line-height: 40px;
      text-align: center;
      padding: 0;
      margin: 0;
    }

    @include e(spinner-item) {
      cursor: pointer;
      &.active {
        background: $--color-background-secondary-hover
      }
      &:hover {
        background: $--color-background-secondary;
      }
    }

    @include e(footer) {
      padding: 8px 8px 0;
      text-align: right;
    }
  }
  @media (forced-colors: active) {
    .zm-time-picker-panel__spinner-item:hover,
    .zm-time-picker-panel__spinner-item.active{
      outline: dotted thin !important;
      outline-offset: -6px;
    }
  }
</style>
